import { localizePath as __lp } from "@/i18n/locales";
import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import FAQSection from "@/components/FAQSection";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ScrollReveal } from "@/components/ScrollReveal";
import BlueprintGenerator from "@/components/BlueprintGenerator";
import InlineLeadForm from "@/components/InlineLeadForm";

import { getLeadMagnet } from "@/data/leadMagnets";
import { TIERS, PRICING_COPY } from "@/data/pricing";

// Landing pages that show the shared project price ranges + link to /pricing.
const PRICED_PAGES = new Set(["custom-software-development"]);
const PRICE_BLOCK = {
  en: { kicker: "// price_ranges", title: "What custom software development costs", body: "Typical ranges per project type. A binding fixed price follows the concept phase, once the scope is truly settled.", weeks: "weeks", link: "See all prices & details" },
  de: { kicker: "// price_ranges", title: "Was individuelle Softwareentwicklung kostet", body: "Typische Spannen je Projektart. Einen verbindlichen Festpreis nennen wir nach der Konzeptphase, wenn der Umfang wirklich feststeht.", weeks: "Wochen", link: "Alle Kosten & Details ansehen" },
  el: { kicker: "// price_ranges", title: "Πόσο κοστίζει η ανάπτυξη εξατομικευμένου λογισμικού", body: "Τυπικά εύρη ανά είδος έργου. Δεσμευτική σταθερή τιμή δίνουμε μετά τη φάση σχεδιασμού, όταν το αντικείμενο έχει οριστεί.", weeks: "εβδομάδες", link: "Όλα τα κόστη & λεπτομέρειες" },
} as const;
const fmtK = (n: number, lang: string) => `${(n * 1000).toLocaleString(lang === "en" ? "en-GB" : lang === "el" ? "el-GR" : "de-DE")} €`;
import {
  ArrowRight,
  Sparkles,
  Check,
  X,
  Lock,
  SlidersHorizontal,
  TrendingUp,
  Puzzle,
  Workflow,
  Zap,
  Database,
  Gauge,
  FileText,
  Code2,
  Eye,
  Rocket,
  Layout,
  ShieldCheck,
  Boxes,
  Target,
  Feather,
  Layers,
  type LucideIcon,
} from "lucide-react";
import { scrollToSection } from "@/lib/scrollToSection";
import { trackConversion } from "@/lib/tracking";
import { trackAnalyticsConversion } from "@/lib/analytics";
import { useLocale } from "@/i18n/LocaleProvider";
import { useTranslation } from "react-i18next";
import { LANG_HREFLANG } from "@/i18n/locales";

const iconMap: Record<string, LucideIcon> = {
  Lock,
  SlidersHorizontal,
  TrendingUp,
  Puzzle,
  Workflow,
  Zap,
  Database,
  Gauge,
  FileText,
  Code2,
  Eye,
  Rocket,
  Layout,
  ShieldCheck,
  Boxes,
  Target,
  Feather,
  Layers,
  Sparkles,
};

/** Split a [[bracketed]] phrase out of a headline so it can be gradient-styled. */
const renderTitle = (title: string) => {
  const parts = title.split(/\[\[(.+?)\]\]/);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="gradient-text">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};



const LeadMagnet = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { lang, localize } = useLocale();
  const { t } = useTranslation();
  const page = getLeadMagnet(slug, lang);

  // Track lead-magnet landing-page views as a soft conversion signal.
  useEffect(() => {
    if (page) {
      trackConversion("lead_magnet_view", { content_name: page.slug });
      trackAnalyticsConversion("lead_magnet_view", page.slug);
    }
  }, [page]);

  const goToBlueprint = () => {
    // The blueprint engine lives on the home page.
    if (!scrollToSection("blueprint")) navigate(localize("/#blueprint"));
  };

  const jsonLd = useMemo(() => {
    if (!page) return undefined;
    const SITE = "https://devcore.systems";
    const langPath = lang === "en" ? "" : `/${lang}`;
    const canonical = `${SITE}${__lp(`/pages/${page.slug}`, lang)}`;
    const logo = `${SITE}/favicon.png`;
    const ogImage = `${SITE}/og-image.png`;
    // Clean, human-readable service name from the headline (strip [[ ]] markers).
    const plainTitle = page.title.replace(/\[\[(.+?)\]\]/g, "$1").trim();

    const organization = {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "DevCore",
      url: `${SITE}/`,
      logo: { "@type": "ImageObject", url: logo },
      sameAs: ["https://www.devcore.systems", "https://www.linkedin.com/company/devcore-websystems"],
      description:
        "DevCore is a custom software studio building bespoke SaaS, portals, web apps, CRMs and AI tools.",
    };

    const webSite = {
      "@type": "WebSite",
      "@id": `${SITE}/#website`,
      name: "DevCore",
      url: `${SITE}/`,
      publisher: { "@id": `${SITE}/#organization` },
    };

    return [
      organization,
      webSite,
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        name: page.metaTitle,
        headline: plainTitle,
        description: page.metaDescription,
        url: canonical,
        inLanguage: LANG_HREFLANG[lang],
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${canonical}#service` },
        breadcrumb: { "@id": `${canonical}#breadcrumb` },
        primaryImageOfPage: { "@type": "ImageObject", url: ogImage },
        publisher: { "@id": `${SITE}/#organization` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "h2"],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("leadPage.home"), item: `${SITE}${langPath}/` },
          { "@type": "ListItem", position: 2, name: plainTitle, item: canonical },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: plainTitle,
        serviceType: "Custom software development",
        description: page.metaDescription,
        url: canonical,
        provider: { "@id": `${SITE}/#organization` },
        areaServed: { "@type": "Place", name: "Worldwide" },
        audience: { "@type": "BusinessAudience", name: "Businesses and teams" },
        ...(PRICED_PAGES.has(page.slug)
          ? {
              offers: TIERS.map((tier) => ({
                "@type": "Offer",
                name: PRICING_COPY[lang].tiers[tier.id].name,
                description: PRICING_COPY[lang].tiers[tier.id].desc,
                url: `${SITE}${__lp("/pricing", lang)}`,
                priceSpecification: {
                  "@type": "PriceSpecification",
                  priceCurrency: "EUR",
                  minPrice: tier.price[0] * 1000,
                  maxPrice: tier.price[1] * 1000,
                },
              })),
            }
          : {}),
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: page.pillarsTitle,
          itemListElement: page.pillars.map((pillar) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: pillar.title,
              description: pillar.body,
            },
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${canonical}#faq`,
        mainEntity: page.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, [page, lang, t]);


  if (!page) return <Navigate to={localize("/404")} replace />;

  const canonical = `https://devcore.systems/pages/${page.slug}`;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${page.metaTitle}`}
        description={page.metaDescription}
        canonical={canonical}
        ogType="website"
        ogImage="https://devcore.systems/og-image.png"
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* ===== HERO ===== */}
      <header className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20 md:pt-44 md:pb-28">
        <div className="absolute inset-0 tech-grid opacity-[0.12] pointer-events-none" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-1 left-[-10%] top-[-10%] w-[55vw] h-[55vw]" />
          <div className="aurora-blob aurora-2 right-[-15%] top-[10%] w-[50vw] h-[50vw]" />
        </div>
        <div
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-background"
        />

        <div className="container relative z-10">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <li>
                <Link to={localize("/")} className="hover:text-accent transition-colors">{t("leadPage.home")}</Link>
              </li>
              <li aria-hidden className="text-border">/</li>
              <li className="text-foreground truncate max-w-[70vw]">{page.eyebrow.replace("// ", "")}</li>
            </ol>
          </nav>

          <ScrollReveal className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-sm mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              <span className="mono-label text-muted-foreground">{page.eyebrow}</span>
            </div>

            <h1 className="text-[1.9rem] sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-foreground">
              {renderTitle(page.title)}
            </h1>
            <p className="mt-7 text-[15px] sm:text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {page.subtitle}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              {page.adLanding ? (
                <>
                  <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                    <Link
                      to={localize("/blueprint")}
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-semibold text-lg transition-transform duration-200 active:scale-[0.97] group"
                    >
                      <Sparkles size={18} />
                      {t("leadPage.adPrimaryCta")}
                      <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </span>
                  <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                    <Link
                      to={localize("/contact")}
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-card/40 text-foreground font-semibold text-lg transition-transform duration-200 active:scale-[0.97]"
                    >
                      {t("leadPage.adSecondaryCta")}
                    </Link>
                  </span>
                </>
              ) : (
                <>
                  <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                    <a
                      href="#lead-form"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
                      }}
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-semibold text-lg transition-transform duration-200 active:scale-[0.97] group"
                    >
                      {t("leadPage.heroPrimaryCta")}
                      <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </span>

                  <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                    <a
                      href="#blueprint"
                      onClick={(e) => {
                        e.preventDefault();
                        goToBlueprint();
                      }}
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-card/40 text-foreground font-semibold text-lg transition-transform duration-200 active:scale-[0.97]"
                    >
                      <Sparkles size={18} className="text-accent" />
                      {t("leadPage.heroSecondaryCta")}
                    </a>
                  </span>
                </>
              )}
            </div>
            {page.adLanding && (
              <p className="font-mono text-xs tracking-[0.15em] text-muted-foreground/60 mt-5">
                {t("leadPage.adHeroNote")}
              </p>
            )}
          </ScrollReveal>

          {/* Stats */}
          <ScrollReveal delay={150} className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {page.stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-border bg-card/40 backdrop-blur-sm p-6 text-center"
              >
                <p className="text-[1.9rem] sm:text-4xl md:text-5xl font-bold gradient-text font-heading">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-2">{stat.label}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </header>

      {/* ===== PROBLEM ===== */}
      <section className="relative py-14 sm:py-20 md:py-28 border-t border-border">
        <div className="container">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-start">
            <ScrollReveal>
              <p className="mono-label mb-4">{page.problemKicker}</p>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                {page.problemTitle}
              </h2>
              <p className="mt-6 text-[15px] sm:text-lg text-muted-foreground leading-relaxed">{page.problemBody}</p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <ul className="space-y-3">
                {page.problemPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 rounded-xl border border-border bg-card/30 p-4"
                  >
                    <span className="mt-0.5 shrink-0 rounded-md bg-destructive/10 p-1.5">
                      <X size={15} className="text-destructive" />
                    </span>
                    <span className="text-foreground/90">{point}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ===== PILLARS (bento) ===== */}
      <section className="relative py-14 sm:py-20 md:py-28 bg-secondary/20 border-t border-border overflow-hidden">
        <div className="absolute inset-0 tech-dots opacity-[0.12] pointer-events-none" />
        <div className="container relative z-10">
          <ScrollReveal className="max-w-2xl mb-14">
            <p className="mono-label mb-4">{page.pillarsKicker}</p>
            <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
              {page.pillarsTitle}
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 gap-5">
            {page.pillars.map((pillar, i) => {
              const Icon = iconMap[pillar.icon] ?? Sparkles;
              return (
                <ScrollReveal key={pillar.title} delay={i * 90}>
                  <article className="group/card relative h-full overflow-hidden rounded-2xl border border-border bg-card/50 p-7 backdrop-blur-sm transition-colors duration-300 hover:border-accent/50">
                    <span className="card-sheen pointer-events-none absolute inset-0 overflow-hidden rounded-2xl" />
                    <div className="relative z-10">
                      <span className="inline-flex rounded-xl border border-border bg-background/60 p-3 text-accent">
                        <Icon size={22} />
                      </span>
                      <h3 className="mt-5 text-xl font-bold text-foreground">{pillar.title}</h3>
                      <p className="mt-3 text-muted-foreground leading-relaxed">{pillar.body}</p>
                    </div>
                  </article>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== COMPARISON ===== */}
      <section className="relative py-14 sm:py-20 md:py-28 border-t border-border">
        <div className="container">
          <ScrollReveal className="max-w-2xl mb-12">
            <p className="mono-label mb-4">{page.compareKicker}</p>
            <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
              {page.compareTitle}
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            {/* Desktop / tablet: 3-column comparison table */}
            <div className="hidden md:block overflow-hidden rounded-2xl border border-border">
              {/* header row */}
              <div className="grid grid-cols-[1.2fr_1fr_1fr] bg-card/60">
                <div className="p-4 md:p-5" />
                <div className="p-4 md:p-5 text-center border-l border-border">
                  <span className="mono-label text-muted-foreground normal-case tracking-normal">
                    {page.compareLeftLabel}
                  </span>
                </div>
                <div className="p-4 md:p-5 text-center border-l border-border bg-primary/10">
                  <span className="mono-label text-accent normal-case tracking-normal">
                    {page.compareRightLabel}
                  </span>
                </div>
              </div>
              {page.compareRows.map((row, i) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-[1.2fr_1fr_1fr] text-sm md:text-base ${
                    i % 2 === 0 ? "bg-background" : "bg-card/20"
                  }`}
                >
                  <div className="p-4 md:p-5 font-medium text-foreground border-t border-border">
                    {row.label}
                  </div>
                  <div className={`p-4 md:p-5 border-t border-l border-border flex items-start gap-2 ${row.leftPositive ? "text-foreground" : "text-muted-foreground"}`}>
                    {row.leftPositive ? (
                      <Check size={16} className="mt-0.5 shrink-0 text-success" />
                    ) : (
                      <X size={16} className="mt-0.5 shrink-0 text-destructive/70" />
                    )}
                    <span>{row.left}</span>
                  </div>
                  <div className="p-4 md:p-5 border-t border-l border-border text-foreground bg-primary/[0.06] flex items-start gap-2">
                    <Check size={16} className="mt-0.5 shrink-0 text-success" />
                    <span>{row.right}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: stacked cards — one per criterion, the two options compared below */}
            <div className="md:hidden space-y-4">
              {page.compareRows.map((row) => (
                <div
                  key={row.label}
                  className="overflow-hidden rounded-2xl border border-border bg-card/30"
                >
                  <div className="px-4 py-3 border-b border-border bg-card/60 font-semibold text-foreground">
                    {row.label}
                  </div>
                  <div className="p-4 flex items-start gap-3">
                    {row.leftPositive ? (
                      <Check size={16} className="mt-0.5 shrink-0 text-success" />
                    ) : (
                      <X size={16} className="mt-0.5 shrink-0 text-destructive/70" />
                    )}
                    <div className="min-w-0">
                      <span className="mono-label text-muted-foreground normal-case tracking-normal block mb-0.5 text-xs">
                        {page.compareLeftLabel}
                      </span>
                      <span className={`text-sm ${row.leftPositive ? "text-foreground" : "text-muted-foreground"}`}>
                        {row.left}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 flex items-start gap-3 border-t border-border bg-primary/[0.06]">
                    <Check size={16} className="mt-0.5 shrink-0 text-success" />
                    <div className="min-w-0">
                      <span className="mono-label text-accent normal-case tracking-normal block mb-0.5 text-xs">
                        {page.compareRightLabel}
                      </span>
                      <span className="text-sm text-foreground">{row.right}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="relative py-14 sm:py-20 md:py-28 bg-secondary/20 border-t border-border overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />
        <div className="container relative z-10">
          <ScrollReveal className="max-w-2xl mb-14">
            <p className="mono-label mb-4">{page.processKicker}</p>
            <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
              {page.processTitle}
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {page.steps.map((step, i) => (
              <ScrollReveal key={step.title} delay={i * 90}>
                <div className="relative h-full rounded-2xl border border-border bg-card/40 p-6 backdrop-blur-sm">
                  <span className="font-mono text-sm text-accent/70">0{i + 1}</span>
                  <h3 className="mt-3 text-lg font-bold text-foreground">{step.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Blueprint generator =====
          The CTA copy promises a roadmap, tech stack and rough spec
          ("Pflichtenheft"), so the generator that actually produces it lives on
          the page instead of only being linked back to the home page. */}
      {PRICED_PAGES.has(page.slug) && (
        <section id="price-ranges" className="relative py-14 sm:py-20 md:py-28 border-t border-border">
          <div className="container relative z-10">
            <ScrollReveal className="max-w-2xl mb-10">
              <p className="mono-label mb-4">{PRICE_BLOCK[lang].kicker}</p>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                {PRICE_BLOCK[lang].title}
              </h2>
              <p className="mt-4 text-[15px] sm:text-lg text-muted-foreground leading-relaxed">{PRICE_BLOCK[lang].body}</p>
            </ScrollReveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {TIERS.map((tier, i) => (
                <ScrollReveal key={tier.id} delay={i * 70}>
                  <div className="h-full rounded-2xl border border-border bg-card/40 p-5 backdrop-blur-sm">
                    <h3 className="text-base font-bold text-foreground">{PRICING_COPY[lang].tiers[tier.id].name}</h3>
                    <p className="mt-3 font-mono text-accent text-sm">
                      {fmtK(tier.price[0], lang)} – {fmtK(tier.price[1], lang)}
                    </p>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {tier.weeks[0]}–{tier.weeks[1]} {PRICE_BLOCK[lang].weeks}
                    </p>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{PRICING_COPY[lang].tiers[tier.id].desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
            <Link
              to={localize("/pricing")}
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
            >
              {PRICE_BLOCK[lang].link} <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      <BlueprintGenerator redirectOnResult />

      {/* ===== FAQ ===== */}
      <FAQSection
        id="lead-faq"
        label={page.faqKicker}
        headingA={page.faqTitle}
        items={page.faqs.map((faq) => ({ question: faq.q, answer: faq.a }))}
      />


      {/* ===== CTA ===== */}
      <section className="relative py-16 sm:py-24 md:py-32 bg-secondary/30 border-t border-border overflow-hidden">
        <div className="absolute inset-0 tech-dots opacity-[0.15] pointer-events-none" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-3 left-1/2 top-1/2 w-[60vw] h-[60vw] -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="container relative z-10">
          <ScrollReveal className="max-w-2xl mx-auto text-center">
            <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold text-foreground leading-tight tracking-tight">
              {page.ctaTitle}
            </h2>
            <p className="mt-5 text-[15px] sm:text-lg text-muted-foreground leading-relaxed">{page.ctaBody}</p>

            <InlineLeadForm
              id="lead-form"
              source={`lead:${page.slug}`}
              sourceLabel={page.title.replace(/\[\[|\]\]/g, "")}
              className="mt-9"
            />

            <div className="mt-6 flex justify-center">
              <a
                href="#blueprint"
                onClick={(e) => {
                  e.preventDefault();
                  goToBlueprint();
                }}
                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-accent transition-colors"
              >
                <Sparkles size={16} className="text-accent" />
                {t("leadPage.ctaSecondary")}
              </a>
            </div>
          </ScrollReveal>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeadMagnet;
